import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";
import classNames from "classnames";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import {
  Typography,
  Box,
  Radio as RadioBase,
  RadioGroup,
  FormControlLabel as FormControlLabelBase,
  FormControl,
  FormLabel,
} from "@material-ui/core";
import { RadioButtonChecked, RadioButtonUnchecked } from "@material-ui/icons";
import SvgIcon from "../Typography/SvgIcon";
import ErrorMessage from "./common/ErrorMessage";
import { getError } from "../../utils/validations";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 2),
    },
  },
  label: {
    fontSize: theme.typography.pxToRem(30),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(2.5),
    marginLeft: theme.spacing(0.25),
    [theme.breakpoints.up("md")]: {
      fontSize: theme.typography.pxToRem(32),
      color: theme.palette.text.primary,
    },
  },
  optionContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.shape.borderRadiusSecondary,
    cursor: "pointer",
    "&.checked, &:hover": {
      background: theme.palette.button.main,
    },
    "&.checked $icon path, &.checked $iconCustom path": {
      fill: theme.palette.text.secondary,
    },
    "&:hover $icon path, &:hover $iconCustom path": {
      fill: theme.palette.text.secondary,
    },
    "&.checked span, &:hover span": {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold
    },
    "&.checked span": {
      fontWeight: theme.typography.fontWeightBold,
    },
    [theme.breakpoints.down("sm")]: {
      boxShadow: theme.shadows[1],
    },
    [theme.breakpoints.up("sm")]: {
      border: "1px solid",
      borderColor: theme.palette.primary.main,
      "&.checked, &:hover": {
        backgroundColor: theme.palette.button.main,
      },
    },
  },
  iconWrapper: {
    display: "inline-flex",
    padding: theme.spacing(0.75, 0),
  },
  icon: {
    "& path": {
      fill: theme.palette.primary.main,
    },
  },
  iconCustomWrapper: {
    display: "inline-flex",
    width: "30px",
    paddingTop: theme.spacing(0.25),
    marginRight: theme.spacing(0.75),
  },
  iconCustom: {
    margin: theme.spacing(0, "auto"),
    "& path": {
      fill: theme.palette.text.alternative,
    },
  },
}));

const FormControlLabel = withStyles((theme) => ({
  root: {
    margin: 0,
  },
  label: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: theme.typography.pxToRem(18),
    color: theme.palette.common.black,
  },
}))(FormControlLabelBase);

const Radio = withStyles((theme) => ({
  root: {
    marginRight: theme.spacing(1.5),
    padding: 0,
  },
}))(RadioBase);

const Icon = ({ custom, type, classes, text }) => {
  if (custom) {
    return (
      <span className={classes.iconCustomWrapper}>
        <SvgIcon type={type} className={classes.iconCustom} title={text} />
      </span>
    );
  }

  return (
    <span className={classes.iconWrapper}>
      <RadioButtonUnchecked
        className={classes.icon}
        fontSize="small"
        title={text}
      />
    </span>
  );
};

const CheckedIcon = ({ custom, type, classes, text }) => {
  if (custom) {
    return (
      <span className={classes.iconCustomWrapper}>
        <SvgIcon type={type} className={classes.iconCustom} title={text} />
      </span>
    );
  }

  return (
    <span className={classes.iconWrapper}>
      <RadioButtonChecked
        className={classes.icon}
        fontSize="small"
        title={text}
      />
    </span>
  );
};

const RadioField = ({
  inputType,
  name,
  label,
  choices,
  validations,
  onSubmit,
}) => {
  const classes = useStyles();
  const {
    register,
    unregister,
    watch,
    setValue,
    getValues,
    handleSubmit,
  } = useFormContext();
  const watchValue = watch(name) ?? "";

  useEffect(() => {
    register(name, getError({ inputType, validations }));
    return () => {
      unregister(name, validations);
    };
  }, [inputType, name, validations, register, unregister]);

  const submit = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleChange = useCallback(
    (e) => {
      setValue(name, e.target.value);

      const isOnlyField = Object.keys(getValues()).length === 1;

      if (isOnlyField) {
        submit();
      }
    },
    [name, setValue, getValues, submit]
  );

  const handleClick = useCallback(
    (key) => {
      return () => {
        setValue(name, key);

        const isOnlyField = Object.keys(getValues()).length === 1;

        if (isOnlyField) {
          submit();
        }
      };
    },
    [name, setValue, getValues, submit]
  );

  return (
    <FormControl component="fieldset" className={classes.root} fullWidth>
      <FormLabel component="legend">
        <Typography className={classes.label} variant="h2" component="h2">
          {label}
        </Typography>
      </FormLabel>

      <RadioGroup
        aria-label={label}
        name={name}
        value={watchValue}
        onChange={handleChange}
      >
        {choices.map(({ key, text }) => {
          const isChecked = watchValue === key;

          return (
            <Box
              key={key}
              className={classNames(classes.optionContainer, {
                checked: isChecked,
              })}
              onClick={handleClick(key)}
            >
              <FormControlLabel
                control={
                  <Radio
                    color="default"
                    icon={
                      <Icon
                        custom={true}
                        type={key}
                        classes={classes}
                        title={text}
                      />
                    }
                    checkedIcon={
                      <CheckedIcon
                        custom={true}
                        type={key}
                        classes={classes}
                        title={text}
                      />
                    }
                  />
                }
                value={key}
                label={text}
              />
            </Box>
          );
        })}
      </RadioGroup>

      <ErrorMessage name={name} />
    </FormControl>
  );
};

RadioField.propTypes = {
  inputType: PropTypes.string.isRequired,
  choices: PropTypes.array,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  validations: PropTypes.object,
};

export default RadioField;
